import {
  APIProvider,
  AdvancedMarker,
  ControlPosition,
  Map,
  MapControl,
  Marker,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { Fragment, useEffect, useRef, useState } from "react";
import MapHandler from "./map-handler";
import { CustomMapControl } from "./map-control";
import { Button } from "primereact/button";

interface inputPrams {
  locationGps: string;
  setLocationGps: React.Dispatch<string>;
  locationLabel: string;
  setLocationLabel: React.Dispatch<string>;
  setVisable: React.Dispatch<boolean>;
}

const API_KEY = "AIzaSyB4m5Hx4i4F8ip8TG9hfuqZCNJ11J05Y_M";

// Search component for location search
function PlaceAutocomplete({
  onLocationSelect,
  initialValue,
}: {
  onLocationSelect: (location: { lat: number; lng: number; address: string }) => void;
  initialValue?: string;
}) {
  const map = useMap();
  const places = useMapsLibrary("places");
  const [searchValue, setSearchValue] = useState(initialValue || "");
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const autocompleteService = useRef<google.maps.places.AutocompleteService>();
  const placesService = useRef<google.maps.places.PlacesService>();

  useEffect(() => {
    if (!places || !map) return;

    autocompleteService.current = new places.AutocompleteService();
    placesService.current = new places.PlacesService(map);
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setSessionToken(undefined);
  }, [places, map]);

  // Update search value when initialValue changes
  useEffect(() => {
    if (initialValue) {
      setSearchValue(initialValue);
    }
  }, [initialValue]);

  const handleInputChange = (value: string) => {
    setSearchValue(value);

    if (!value || !autocompleteService.current || !sessionToken) {
      setPredictions([]);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: value,
        sessionToken,
      },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(predictions);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handlePlaceSelect = (placeId: string) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      {
        placeId,
        fields: ["geometry", "formatted_address"],
        sessionToken,
      },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry?.location
        ) {
          const location = place.geometry.location;
          onLocationSelect({
            lat: location.lat(),
            lng: location.lng(),
            address: place.formatted_address || "",
          });
          setSearchValue(place.formatted_address || "");
          setPredictions([]);
          setSessionToken(new places!.AutocompleteSessionToken());
        }
      }
    );
  };

  return (
    <div
      className="autocomplete-container"
      style={{ position: "relative", width: "100%", marginBottom: "10px" }}
    >
      <input
        type="text"
        value={searchValue}
        onChange={e => handleInputChange(e.target.value)}
        placeholder="Search for a location..."
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      />
      {predictions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderTop: "none",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
          }}
        >
          {predictions.map(prediction => (
            <div
              key={prediction.place_id}
              onClick={() => handlePlaceSelect(prediction.place_id)}
              style={{
                padding: "10px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "white")}
            >
              {prediction.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Reverse geocoding component
function ReverseGeocoder({
  position,
  onAddressFound,
}: {
  position: { lat: number; lng: number };
  onAddressFound: (address: string) => void;
}) {
  const map = useMap();
  const geocoder = useRef<google.maps.Geocoder>();

  useEffect(() => {
    if (!map) return;
    geocoder.current = new google.maps.Geocoder();
  }, [map]);

  useEffect(() => {
    if (!geocoder.current || !position) return;

    geocoder.current.geocode({ location: position }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        onAddressFound(results[0].formatted_address);
      }
    });
  }, [position, onAddressFound]);

  return null;
}

// Component to handle map centering
function MapController({ center }: { center: { lat: number; lng: number } }) {
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  return null;
}

export default function GoogleMapsSelect(props: inputPrams) {
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>({
    lat: 40.7128,
    lng: -74.006,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string>("");

  // Request user's current location on component mount
  useEffect(() => {
    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by this browser.");
        return;
      }

      setIsLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          const newPosition = { lat: latitude, lng: longitude };
          setMarkerPosition(newPosition);
          props.setLocationGps(`${latitude},${longitude}`);
          setIsLoadingLocation(false);
        },
        error => {
          console.error("Error getting location:", error);
          setLocationError("Unable to retrieve your location. Using default location.");
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    };

    // Only get current location if no existing location is provided AND no location label exists
    if (!props.locationGps && !props.locationLabel) {
      getCurrentLocation();
    }
  }, []);

  // Initialize marker position from props if available
  useEffect(() => {
    if (props.locationGps) {
      try {
        const [lat, lng] = props.locationGps.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMarkerPosition({ lat, lng });
        }
      } catch (error) {
        console.error("Error parsing locationGps:", error);
      }
    }
  }, [props.locationGps]);

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setMarkerPosition(location);
    props.setLocationGps(`${location.lat},${location.lng}`);
    props.setLocationLabel(location.address);
  };

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setMarkerPosition(newPosition);
      props.setLocationGps(`${newPosition.lat},${newPosition.lng}`);
      setIsDragging(false);
    }
  };

  const handleAddressFound = (address: string) => {
    if (!isDragging) {
      props.setLocationLabel(address);
    }
  };

  const handleConfirm = () => {
    props.setVisable(false);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser.");
      return;
    }

    setIsLoadingLocation(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        const newPosition = { lat: latitude, lng: longitude };
        setMarkerPosition(newPosition);
        props.setLocationGps(`${latitude},${longitude}`);
        setIsLoadingLocation(false);
      },
      error => {
        console.error("Error getting location:", error);
        setLocationError("Unable to retrieve your location. Please try again or search manually.");
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // 1 minute
      }
    );
  };

  return (
    <Fragment>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            width: "90%",
            maxWidth: "800px",
            height: "80%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <h3 style={{ margin: "0 0 10px 0" }}>Select Location</h3>
            {locationError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginBottom: "10px",
                }}
              >
                {locationError}
              </div>
            )}
          </div>

          <APIProvider apiKey={API_KEY}>
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <div style={{ flex: 1 }}>
                <PlaceAutocomplete
                  onLocationSelect={handleLocationSelect}
                  initialValue={props.locationLabel}
                />
              </div>
              <Button
                icon="pi pi-map-marker"
                onClick={handleGetCurrentLocation}
                disabled={isLoadingLocation}
                style={{ whiteSpace: "nowrap", color: "grey" }}
                outlined
                size="small"
              />
            </div>

            <div style={{ flex: 1, minHeight: "400px", marginBottom: "10px" }}>
              <Map
                defaultZoom={13}
                center={markerPosition}
                mapId="location-picker-map"
                style={{ width: "100%", height: "100%" }}
              >
                <MapController center={markerPosition} />
                <AdvancedMarker
                  position={markerPosition}
                  draggable={true}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={handleMarkerDragEnd}
                >
                  <Pin background={"red"} glyphColor={"#000"} borderColor={"#000"} />
                </AdvancedMarker>

                <ReverseGeocoder position={markerPosition} onAddressFound={handleAddressFound} />
              </Map>
            </div>

            <div
              style={{
                padding: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                marginBottom: "10px",
                fontSize: "14px",
              }}
            >
              <strong>Selected Address:</strong> {props.locationLabel || "Loading address..."}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <Button label="Cancel" severity="secondary" onClick={() => props.setVisable(false)} />
              <Button label="Confirm Location" onClick={handleConfirm} />
            </div>
          </APIProvider>
        </div>
      </div>
    </Fragment>
  );
}
